/**
 * Centralized constants for visualization overlays
 * Consolidates hardcoded values from overlay components
 */

// Score thresholds used across all overlays
export const SCORE_THRESHOLDS = {
  GOOD: 80,      // Green zone
  MEDIUM: 60,    // Yellow zone
  // Below 60 = Red zone
} as const;

// Score-based colors (consistent across all overlays)
export const SCORE_COLORS = {
  GOOD: '#14b8a6',    // teal-500
  MEDIUM: '#fbbf24',  // amber-400
  POOR: '#ef4444',    // red-500
  NEUTRAL: '#808080', // grey for non-annotated landmarks
} as const;

// Deviation thresholds for arrows/vectors (normalized 0-1 distance)
export const DEVIATION_THRESHOLDS = {
  EXCELLENT: 0.05,   // Green arrow (5% distance)
  GOOD: 0.10,        // Yellow arrow (10% distance)
  MODERATE: 0.15,    // Orange arrow (15% distance)
  // Above 0.15 (15%) = Red arrow
} as const;

// Deviation vector colors
export const DEVIATION_COLORS = {
  EXCELLENT: '#22c55e', // green-500
  GOOD: '#eab308',      // yellow-500
  MODERATE: '#f97316',  // orange-500
  POOR: '#ef4444',      // red-500
} as const;

// Alignment rings configuration
export const ALIGNMENT_RINGS = {
  RADII: [15, 30, 45] as const, // pixels
  COLORS: ['#22c55e', '#eab308', '#f97316', '#ef4444'] as const, // inner to outer to outside
  RING_COLOR: '#14b8a6', // teal-500 for target rings
  LINE_WIDTH: 1.5,
  RING_OPACITIES: [1.0, 0.6, 0.3] as const, // inner to outer
} as const;

// Ghost echo configuration
export const GHOST_ECHO = {
  COLOR: '#14b8a6',
  OPACITY: 0.4,
  LANDMARK_RADIUS: 4,
  CONNECTION_WIDTH: 2,
  GLOW_BLUR: 8,
} as const;

// Ghost outline visual differentiation (dashed, hollow, thicker)
export const GHOST_OUTLINE = {
  LINE_WIDTH: 3,
  DASH_PATTERN: [6, 4] as number[],
  LANDMARK_RADIUS: 6,
  GLOW_BLUR: 14,
  HIP_INDICES: [23, 24] as const,
  SHOULDER_INDICES: [11, 12] as const,
} as const;

// Thermal silhouette configuration
export const THERMAL_SILHOUETTE = {
  SCAN_LINE_SPACING: 4, // pixels
  MIN_HULL_POINTS: 3,
  FILL_OPACITY_MULTIPLIER: 0.6,
} as const;

// Visibility threshold (skip landmarks below this visibility)
export const VISIBILITY_THRESHOLD = 0.5;

// Rendering thresholds
export const RENDERING = {
  MIN_INTENSITY: 5, // Skip rendering if intensity below this (0-100 scale)
  MIN_ALPHA: 0.3,   // Minimum alpha value for overlays
} as const;

// Canvas drawing constants
export const CANVAS_DRAWING = {
  LANDMARK_RADIUS: 4,
  LINE_WIDTH: 2,
  BORDER_LINE_WIDTH: 1,
  ARROW_HEAD_LENGTH: 8,
  GLOW_BLUR_SOFT: 6,
  GLOW_BLUR_MEDIUM: 8,
  GLOW_BLUR_STRONG: 10,
} as const;

// Common colors
export const COMMON_COLORS = {
  WHITE: '#ffffff',
  NEUTRAL_GRAY: '#9ca3af', // gray-400
  BORDER_WHITE: '#ffffff',
} as const;

// Particle system constants
export const PARTICLE_SYSTEM = {
  MAX_PARTICLES: 500,
  DRAG_COEFFICIENT: 0.98,
  GLOW_HEX_ALPHA: '40', // 25% opacity for glow effects
  BRIGHT_CENTER_INTENSITY: 70,
  BRIGHT_CENTER_SIZE_RATIO: 0.4,
  FRAME_JUMP_THRESHOLD: 2, // Clear particles if frame changes by more than this
} as const;

// Thermal silhouette constants (additional to existing)
export const THERMAL_EFFECTS = {
  BORDER_GLOW_BLUR: 10,
  GRADIENT_HEX_ALPHA: '40', // Fade out alpha for gradients
  SCAN_LINE_OPACITY: 0.15,
  SCAN_LINE_INTENSITY_THRESHOLD: 50,
  SCAN_LINE_WIDTH: 1,
} as const;

// Helper function to get score color based on threshold-based scoring
// Note: This is different from the gradient-based getScoreColor in utils/colors.ts
export function getThresholdScoreColor(score: number): string {
  if (score >= SCORE_THRESHOLDS.GOOD) return SCORE_COLORS.GOOD;
  if (score >= SCORE_THRESHOLDS.MEDIUM) return SCORE_COLORS.MEDIUM;
  return SCORE_COLORS.POOR;
}

// Helper function to interpolate between two hex colors
function interpolateColor(color1: string, color2: string, t: number): string {
  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);

  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);

  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Helper function to get deviation color with smooth interpolation
export function getDeviationColor(deviationDistance: number): string {
  if (deviationDistance < DEVIATION_THRESHOLDS.EXCELLENT) {
    return DEVIATION_COLORS.EXCELLENT;
  } else if (deviationDistance < DEVIATION_THRESHOLDS.GOOD) {
    // Interpolate green to yellow
    const t = (deviationDistance - DEVIATION_THRESHOLDS.EXCELLENT) / (DEVIATION_THRESHOLDS.GOOD - DEVIATION_THRESHOLDS.EXCELLENT);
    return interpolateColor(DEVIATION_COLORS.EXCELLENT, DEVIATION_COLORS.GOOD, t);
  } else if (deviationDistance < DEVIATION_THRESHOLDS.MODERATE) {
    // Interpolate yellow to orange
    const t = (deviationDistance - DEVIATION_THRESHOLDS.GOOD) / (DEVIATION_THRESHOLDS.MODERATE - DEVIATION_THRESHOLDS.GOOD);
    return interpolateColor(DEVIATION_COLORS.GOOD, DEVIATION_COLORS.MODERATE, t);
  } else {
    // Red for large deviations
    return DEVIATION_COLORS.POOR;
  }
}

// Helper function to check if landmark is visible
export function isLandmarkVisible(landmark: { visibility?: number }): boolean {
  return landmark.visibility === undefined || landmark.visibility >= VISIBILITY_THRESHOLD;
}

// Helper function to calculate opacity from intensity (0-100 scale)
export function getOpacityFromIntensity(intensity: number, minAlpha: number = RENDERING.MIN_ALPHA): number {
  return Math.max(minAlpha, intensity / 100);
}

// Helper function to add hex alpha to color
export function addHexAlpha(color: string, alpha: string): string {
  return color + alpha;
}

// Helper function to get alignment ring zone color
export function getAlignmentZoneColor(zone: number): string {
  // zone: 0 (inner), 1 (middle), 2 (outer), 3 (outside)
  if (zone >= 0 && zone < ALIGNMENT_RINGS.COLORS.length) {
    return ALIGNMENT_RINGS.COLORS[zone];
  }
  return ALIGNMENT_RINGS.COLORS[ALIGNMENT_RINGS.COLORS.length - 1]; // fallback to last color
}

// Goal overlay constants — used by GoalOverlayLayer and goalOverlayHelpers
export const GOAL_OVERLAY = {
  AXIS_LINE_WIDTH: 4,
  AXIS_GLOW_BLUR: 14,
  AXIS_ARC_MAX_RADIUS: 40,
  TIMING_RING_RADIUS: 18,
  TIMING_EVENT_RING_RADIUS: 28,
  TIMING_EVENT_WINDOW: 3,  // frames before/after event to show flash
  SPATIAL_BADGE_SIZE: { width: 72, height: 28 },
  MOVEMENT_LINE_WIDTH: 3,
  MOVEMENT_DOT_RADIUS: 10,
  // Demo-path preview (travel/preview-path-points): the coach's own demo
  // path drawn on the authoring canvas while picking a travel target, with
  // no score to color it by. Reuses the teal already used elsewhere for
  // "this is the template's own reference" (EnhancedEvaluationDisplay's
  // template-skeleton PoseOverlay), rather than inventing a new color.
  PREVIEW_PATH_COLOR: '#4ec9b0',
  BADGE_BG: 'rgba(11, 15, 20, 0.9)',
  BADGE_FONT: 'bold 13px Inter, sans-serif',
  BADGE_PADDING: { x: 8, y: 5 },
} as const;
